import { range } from 'd3-array';
import { getDefaultStepOptions } from './get-default-step-options';

const isNum = x => typeof x === 'number';

/**
 * @param dataExtent - [min, max] array representing the data limits
 * @param totalPixels - total pixels available for the axis to span
 * @param options - extra constraints
 *
 * Opts can contain:
 * fixedMaxValue: force the maximum tick value to this. Takes precedence over looser constraints
 * fixedMinValue: force the minimum tick value to this. Takes precedence over looser constraints
 * fixedNumberOfTicks: force a specific number of ticks. Takes precedence over looser constraints
 * lowestMaxValue: the maximum tick value should be no lower than this
 * highestMaxValue: the maximum tick value should be no higher than this
 * lowestMinValue: the minimum tick value should be no lower than this
 * highestMinValue: the minimum tick value should be no higher than this
 * stepOptions: array of step options the algorithm will try. If none are suitable, will be ignored.
 * padding: padding on each end of the data extent. Default 0.05
 */
export function calculateTickValues(dataExtent, totalPixels, options) {

	// We want to make sure the tick values are not crammed in too close together. Set a minimum
	// number of pixels required in between tick values.
	const minPixelsPerStep = 25;

	const opts = options || {};

	let dataRange = dataExtent[1] - dataExtent[0];

	// Add padding. For data with no range at all, add a default padding of 1 to each side,
	// otherwise add a padding of 10% of the data range to each side
	const padding = typeof opts.padding === 'number' ? opts.padding : 0.05;
	const paddedDataExtent = [
		dataExtent[0] - (dataRange === 0 ? 1 : padding * dataRange),
		dataExtent[1] + (dataRange === 0 ? 1 : padding * dataRange)
	];

	// Minimum constraints
	const min = {
		data: dataExtent[0],
		dataPadded: paddedDataExtent[0],
		fixed: opts.fixedMinValue,
		lowerConstraint: opts.lowestMinValue,
		upperConstraint: opts.highestMinValue
	};

	// Maximum constraints
	const max = {
		data: dataExtent[1],
		dataPadded: paddedDataExtent[1],
		fixed: opts.fixedMaxValue,
		lowerConstraint: opts.lowestMaxValue,
		upperConstraint: opts.highestMaxValue
	};

	// Get the bounds of the range the axis may span
	const rangeBounds = getRangeBounds(min, max);

	let ticks = null;

	if (Array.isArray(opts.stepOptions)) {

		// A set of custom step options has been explicitly provided, so try to find a good set of tick
		// values using these options.
		opts.stepOptions.sort((a, b) => a - b);
		ticks = findTicksBasedOnStepOptions(opts.stepOptions, opts, min, max, rangeBounds, totalPixels, minPixelsPerStep);

	}

	if (ticks === null) {

		// Either no custom step options were given, or none of the custom steps satisfied all of the constraints.
		// Try again with default steps.
		const steps = getDefaultStepOptions(rangeBounds.min[1], rangeBounds.max[0]);
		ticks = findTicksBasedOnStepOptions(steps, opts, min, max, rangeBounds, totalPixels, minPixelsPerStep);

	}

	if (ticks === null) {

		// still can't find step to meet constraints. Instead just split the range out ignoring some constraints.
		// Most important is number of ticks. If that is not present, just split it out so none are too close.
		const numTicks = isNum(opts.fixedNumberOfTicks)
			? opts.fixedNumberOfTicks
			: Math.floor(totalPixels / minPixelsPerStep);

		const step = (rangeBounds.max[0] - rangeBounds.min[1]) / (numTicks - 1);
		ticks = range(rangeBounds.min[1], rangeBounds.max[0] + step * 0.1, step);

	}

	return ticks;

}

/**
 * Find an appropriate set of ticks given an array of step sizes to chose from. If such a set cannot
 * be found, null is returned.
 *
 * @param steps
 * @param opts - extra constraints
 * @param min - object containing min data
 * @param max - object containing max data
 * @param rangeBounds - object containing axis range bounds
 * @param totalPixels - total pixels the axis will span
 * @param minPixelsPerStep - how many pixels should be between each tick, at a minimum
 * @returns {number[] | null}
 */
function findTicksBasedOnStepOptions(steps, opts, min, max, rangeBounds, totalPixels, minPixelsPerStep) {

	// Loop through step options, try the different steps.
	for (let i = 0, n = steps.length; i < n; i++) {

		const step = steps[i];

		if (isNum(min.fixed) && isNum(max.fixed) && (max.fixed - min.fixed) % step !== 0) {
			// This step size cannot work given the fixed min and max values. Try another step.
			continue;
		}

		// start with min tick close as possible to the upper bound of the axis minimum value,
		// or set to the fixed minimum if it has been specified. Make sure the min tick does not
		// go below the absolute minimum value allowed.
		let minTick = isNum(min.fixed)
			? min.fixed
			: Math.max(rangeBounds.min[0], step * Math.floor(rangeBounds.min[1] / step));

		// start with max tick close as possible to the lower bound of the axis maximum value,
		// or set to the fixed maximum if it has been specified. Make sure the max tick does not
		// go above the absolute maximum value allowed.
		let maxTick = isNum(max.fixed)
			? max.fixed
			: Math.min(rangeBounds.max[1], step * Math.ceil(rangeBounds.max[0] / step));

		// Due to constraints, it may now be possible to have a range which doesn't divide evenly
		// by the current step size. For example, data with a max limit of 100 and a step size of 15.
		// Min tick could be calculated as 45, and max would be 105 but gets set to 100 due to an axis
		// constraint. In these cases, try to adjust the min/max ticks to get an evenly divisible range.
		const isRangeDivisible = (maxTick - minTick) % step === 0;

		if (!isRangeDivisible) {

			if (maxTick === rangeBounds.max[1] && maxTick % step > 0) {
				// try adjust min tick down, to make sure the range is divisble by the current step size.
				minTick = maxTick - step * Math.ceil((maxTick - minTick) / step);
			}

			if (minTick === rangeBounds.min[0] && minTick % step > 0) {
				// try adjust max tick up, to make sure the range is divisible by the current step size.
				maxTick = minTick + step * Math.ceil((maxTick - minTick) / step);
			}

		}

		// If the rounded value is now below the lower bound, try next step size.
		if (minTick < rangeBounds.min[0]) {
			continue;
		}

		// If the rounded value is now below the lower bound, try next step size.
		if (maxTick > rangeBounds.max[1]) {
			continue;
		}

		let numSteps = (maxTick - minTick) / step;
		let numTicks = numSteps + 1;

		if (totalPixels / numSteps < minPixelsPerStep) {
			// Ticks are too close together. Try a larger step.
			continue;
		}

		if (isNum(opts.fixedNumberOfTicks)) {

			if (numTicks > opts.fixedNumberOfTicks) {
				// Too many ticks required for this step size, unable to adjust to
				// match the required tick count.
				continue;
			}

			if (numTicks === opts.fixedNumberOfTicks) {
				// Just right!
				return range(minTick, maxTick + step, step);
			}

			// We have less ticks than needed. But we might have some room to work with...
			let canAdjust = 1 + (maxTick - minTick) / step < opts.fixedNumberOfTicks;

			// Start adjusting the min and max ticks until we reach the required number of ticks.
			// Start with the tick which has the smaller deviation from it's respective data bound.
			const adjustMinTickFirst = min.data - minTick <= maxTick - max.data;
			let i = adjustMinTickFirst ? 0 : 1;

			do {

				let canAdjustMin = minTick - step >= rangeBounds.min[0];
				let canAdjustMax = maxTick + step <= rangeBounds.max[1];

				if ((canAdjustMin && !canAdjustMax) || (canAdjustMin && i % 2 === 0)) {

					// Adjust the min tick downwards if either:
					// 1. The max tick is unable to be increased further without breaking constraints
					// 2. For this iteration it is the min ticks turn to be adjusted
					minTick -= step;

				} else if ((canAdjustMax && !canAdjustMin) || (canAdjustMax && i % 2 === 1)) {

					// Adjust the max tick upwards if either:
					// 1. The min tick is unable to be decreased further without breaking constraints
					// 2. For this iteration it is the max ticks turn to be adjusted,
					maxTick += step;

				} else {

					// Neither tick value can be adjusted. An alternative step size will have to be tried.
					canAdjust = false;
					break;

				}

				numTicks += 1;
				numSteps += 1;
				i++;

			} while (numTicks < opts.fixedNumberOfTicks);

			if (!canAdjust) {
				// Unable to adjust ticks to get required count. Try larger step.
				continue;
			}

			if (totalPixels / numSteps < minPixelsPerStep) {
				// Ticks were adjusted, but now they are too close together. Try larger step.
				continue;
			}

		}

		// At this point, our set of ticks are within axis bounds, abide by any tick count requirement that exists,
		// and are spaced out enough.
		return range(minTick, maxTick + step, step);

	}

	// If we are here, none of the steps were good enough to meet all requirements
	return null;

}

/**
 * Calculate the range bounds given the min data object and max data object.
 * It takes into consideration the present constraints on min/max values.
 *
 * @param min
 * @param max
 * @returns {{min: number[], max: number[], extent: number[]}}
 */
function getRangeBounds(min, max) {

	// The lowest minimum value permitted.
	let lowerMin;

	if (isNum(min.fixed)) {
		lowerMin = min.fixed;
	} else {
		if (isNum(min.lowerConstraint)) {
			lowerMin = min.lowerConstraint;
		} else {
			lowerMin = Number.NEGATIVE_INFINITY;
		}
	}

	// The highest minimum value permitted.
	let upperMin;

	if (isNum(min.fixed)) {
		upperMin = min.fixed;
	} else {
		if (isNum(min.upperConstraint)) {
			upperMin = Math.min(min.dataPadded, min.upperConstraint);
		} else {
			upperMin = isNum(min.lowerConstraint)
				? Math.max(min.lowerConstraint, min.dataPadded)
				: min.dataPadded;
		}
	}

	// The lowest maximum value permitted.
	let lowerMax;

	if (isNum(max.fixed)) {
		lowerMax = max.fixed;
	} else {
		if (isNum(max.lowerConstraint)) {
			lowerMax = Math.max(max.dataPadded, max.lowerConstraint);
		} else {
			lowerMax = isNum(max.upperConstraint)
				? Math.min(max.upperConstraint, max.dataPadded)
				: max.dataPadded;
		}
	}

	// The highest maximum value permitted.
	let upperMax;

	if (isNum(max.fixed)) {
		upperMax = max.fixed;
	} else {
		if (isNum(max.upperConstraint)) {
			upperMax = max.upperConstraint;
		} else {
			upperMax = Number.POSITIVE_INFINITY;
		}
	}

	const minExtent = lowerMax - upperMin;
	const maxExtent = upperMax - lowerMin;

	return {
		min: [lowerMin, upperMin],
		max: [lowerMax, upperMax],
		extent: [minExtent, maxExtent]
	};

}



// WEBPACK FOOTER //
// ./utils/panelgraph/functions/calculate-tick-values.js